import { useMemo } from 'react'
import type { Card } from '../../types'
import KLineChart from '../KLine/KLineChart'
import ChoiceButtons from './ChoiceButtons'
import Icon, { type IconName } from '../Common/Icon'
import { generateKLineConfig } from '../../utils/klineGenerator'

interface CardFrontProps {
  card: Card
  onChoice: (index: number) => void
  selectedIndex: number | null
}

function CardFront({ card, onChoice, selectedIndex }: CardFrontProps) {
  // 卡片类型对应的像素图标（Icon 组件，DESIGN §10）
  const typeIcon: Record<string, { icon: IconName; color: string; label: string }> = {
    concept: { icon: 'book', color: 'var(--color-cyan)', label: '概念' },
    decision: { icon: 'sword', color: 'var(--color-gold)', label: '决策' },
    identify: { icon: 'magnifier', color: 'var(--color-cyan)', label: '识别' },
    timing: { icon: 'timer', color: 'var(--color-warning)', label: '择时' },
    score: { icon: 'star', color: 'var(--color-gold)', label: '评分' },
    psychology: { icon: 'brain', color: 'var(--color-bear)', label: '心理' },
  }

  const typeInfo = typeIcon[card.type] ?? typeIcon.decision!
  const difficultyLabel = {
    easy: '简单',
    medium: '普通',
    hard: '困难',
  }[card.difficulty]
  const difficultyColor = {
    easy: 'var(--color-bull)',
    medium: 'var(--color-warning)',
    hard: 'var(--color-bear)',
  }[card.difficulty]

  // K 线图场景预设的中文显示名 (与 klineGenerator.ts 里的预设一一对应)
  const PRESET_LABEL: Record<string, string> = {
    'perfect-b1': '完美 B1',
    'break-white-line': '破白线',
    'fake-b2': '假 B2',
    'consolidation': '震荡整理',
    'healthy-position': '健康位置',
  }

  // K 线图：把 preset 展开为 data
  const klineConfig = useMemo(() => {
    if (!card.front.kline) return null
    if (card.front.kline.data) return card.front.kline  // 已有 data 直接用
    if (card.front.kline.preset) {
      const generated = generateKLineConfig(card.front.kline.preset)
      return generated ?? card.front.kline
    }
    return card.front.kline
  }, [card.front.kline])

  return (
    <div className="p-4">
      {/* ========== 标题栏 ========== */}
      <div className="flex items-start justify-between mb-3 gap-3">
        <h3 className="font-pixel-body text-base font-bold text-[color:var(--color-text-bright)] leading-tight flex-1">
          {card.title}
        </h3>
        <div className="flex flex-col gap-1 items-end">
          <span
            className="pixel-tag inline-flex items-center gap-1"
            style={{ color: typeInfo.color, borderColor: typeInfo.color }}
          >
            <Icon name={typeInfo.icon} size={10} />
            {typeInfo.label}
          </span>
          <span
            className="pixel-tag text-[7px]"
            style={{ color: difficultyColor, borderColor: difficultyColor }}
          >
            {difficultyLabel}
          </span>
        </div>
      </div>

      {/* ========== 场景描述 ========== */}
      <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3">
        <div className="font-pixel text-[9px] text-[color:var(--color-text-dim)] mb-1 tracking-wider">
          ► 场景
        </div>
        <p className="font-pixel-body text-sm text-[color:var(--color-text-bright)] leading-snug">
          {card.front.context}
        </p>
      </div>

      {/* ========== K 线图 ========== */}
      {klineConfig && klineConfig.data && (
        <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-2 mb-3">
          <div className="font-pixel text-[9px] text-[color:var(--color-text-dim)] mb-1 tracking-wider px-1">
            ► K 线图 · {PRESET_LABEL[klineConfig.preset ?? ''] ?? klineConfig.preset}
          </div>
          <KLineChart config={klineConfig} height={240} />
        </div>
      )}

      {/* ========== 信息列表 ========== */}
      <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3">
        <div className="font-pixel text-[9px] text-[color:var(--color-text-dim)] mb-1 tracking-wider">
          ► 信息
        </div>
        <ul className="space-y-1 font-pixel-body text-sm text-[color:var(--color-text)]">
          {card.front.info.map((info, index) => (
            <li key={index} className="flex gap-2">
              <span className="text-[color:var(--color-gold)] flex-shrink-0">▸</span>
              <span>{info}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ========== 提示 ========== */}
      {card.front.hint && (
        <div className="pixel-box bg-[color:var(--color-bg-elevated)] p-3 mb-3 border-[color:var(--color-warning)]">
          <div className="flex gap-2">
            <Icon
              name="bulb"
              size={15}
              className="pixel-blink-soft text-[color:var(--color-warning)] mt-0.5"
            />
            <p className="font-pixel-body text-sm text-[color:var(--color-warning)] leading-snug flex-1">
              {card.front.hint}
            </p>
          </div>
        </div>
      )}

      {/* ========== 选择按钮 ========== */}
      {card.choices.length > 0 && (
        <ChoiceButtons
          choices={card.choices}
          onSelect={onChoice}
          selectedIndex={selectedIndex}
        />
      )}
    </div>
  )
}

export default CardFront